/**
 * SkillsMenu Component
 * 
 * A tab panel for managing user skills with full CRUD functionality.
 * Displays skills stored in Firebase under users -> {userId} -> skills
 * 
 * Features:
 * - Display all user skills with ratings
 * - Add new skills with name, description, and rating
 * - Edit existing skills
 * - Delete skills with confirmation
 * - Real-time Firebase sync
 * - Form validation
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../config/firebaseConfig';
import { loadUserSkills, saveSkill, updateSkill, deleteSkill } from './utilities/skillsUtils';
import FixedMenu from '../../Common/Menus/FixedMenu';
import IconPicker from '../../Common/Icons/IconPicker';
import IconDisplay from '../../Common/Icons/IconDisplay';
import { rewriteSkillWithAI } from '../../AI/utilities/aiLogicService.js';
import FeedbackNotice from '../../Common/Feedback/FeedbackNotice.jsx';

const SkillsMenu = ({
  onOpenAI,
  onSkillsLoaded,
  initialDraft,
  onInitialDraftConsumed,
  onSkillSaved,
  onFormCancelled,
  refreshKey = 0,
  assistantOpen = false,
}) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rating: 3,
    icon: null,
  });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState('details');
  const [fieldErrors, setFieldErrors] = useState({});
  const nameInputRef = useRef(null);
  const descriptionInputRef = useRef(null);

  useEffect(() => {
    if (!initialDraft) return;
    setEditingSkill(null);
    setFormData({
      name: initialDraft.name || '',
      description: initialDraft.description || '',
      rating: Number(initialDraft.rating) || 3,
      icon: initialDraft.icon || null,
    });
    setError(null);
    setFieldErrors({});
    setActiveFormTab('details');
    setShowForm(true);
    onInitialDraftConsumed?.();
  }, [initialDraft, onInitialDraftConsumed]);

  // Get current user ID
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      }
    });

    return unsubscribe;
  }, []);

  // Load skills when userId changes
  useEffect(() => {
    if (userId) {
      loadSkills();
    }
  }, [refreshKey, userId]);

  /**
   * Load all skills for the current user
   */
  const loadSkills = async () => {
    setLoading(true);
    setError(null);
    try {
      const userSkills = await loadUserSkills(userId);
      const normalizedSkills = Array.isArray(userSkills) ? userSkills : [];
      setSkills(normalizedSkills);
      onSkillsLoaded?.(normalizedSkills);
    } catch (err) {
      console.error('Error loading skills:', err);
      setError('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((current) => ({ ...current, [name]: undefined }));
    setFormData({
      ...formData,
      [name]: name === 'rating' ? parseInt(value) : value,
    });
  };

  /**
   * Handle opening add form
   */
  const handleAddSkill = () => {
    setEditingSkill(null);
    setFormData({ name: '', description: '', rating: 3, icon: null });
    setFieldErrors({});
    setActiveFormTab('details');
    setShowForm(true);
  };

  /**
   * Handle editing a skill
   */
  const handleEditSkill = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name,
      description: skill.description,
      rating: skill.rating,
      icon: skill.icon || null,
    });
    setFieldErrors({});
    setActiveFormTab('details');
    setShowForm(true);
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) {
      nextErrors.name = 'Enter a skill name.';
    }

    if (!formData.description.trim()) {
      nextErrors.description = 'Enter a short description.';
    }

    if (formData.rating < 1 || formData.rating > 5) {
      nextErrors.rating = 'Choose a confidence level from 1 to 5.';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const detailsInvalid = nextErrors.name || nextErrors.description;
      setActiveFormTab(detailsInvalid ? 'details' : 'appearance');
      setError('Review the highlighted fields before saving.');
      window.setTimeout(() => {
        if (nextErrors.name) nameInputRef.current?.focus();
        else if (nextErrors.description) descriptionInputRef.current?.focus();
      }, 0);
      return false;
    }
    return true;
  };

  /**
   * Handle form submission (add or update)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (editingSkill) {
        // Update existing skill
        await updateSkill(userId, editingSkill.id, formData);
        setSuccessMessage(`Skill "${formData.name}" updated successfully`);
      } else {
        // Create new skill with timestamp as ID
        const skillId = `skill_${Date.now()}`;
        await saveSkill(userId, skillId, formData);
        setSuccessMessage(`Skill "${formData.name}" added successfully`);
      }

      setShowForm(false);
      setFormData({ name: '', description: '', rating: 3, icon: null });
      await loadSkills();
      onSkillSaved?.();
    } catch (err) {
      console.error('Error saving skill:', err);
      setError(err.message || 'Failed to save skill');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle skill deletion
   */
  const handleDeleteSkill = async (skillId, skillName) => {
    if (window.confirm(`Are you sure you want to delete the skill "${skillName}"?`)) {
      try {
        await deleteSkill(userId, skillId);
        setSuccessMessage('Skill deleted successfully');
        await loadSkills();
      } catch (err) {
        console.error('Error deleting skill:', err);
        setError('Failed to delete skill');
      }
    }
  };

  /**
   * Handle cancel form
   */
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingSkill(null);
    setFormData({ name: '', description: '', rating: 3, icon: null });
    setError(null);
    setFieldErrors({});
    setActiveFormTab('details');
    onFormCancelled?.();
  };

  const handleAIRewrite = useCallback(() => {
    if (!formData.name.trim() && !formData.description.trim()) {
      setError('Add a skill name or description before asking AI to rewrite it.');
      return;
    }
    setError(null);
    onOpenAI?.({
      kind: 'skill',
      label: 'Rewrite skill',
      contextLabel: formData.name.trim() || 'New skill',
      placeholder: 'Describe how this skill wording should improve...',
      generate: (request) => rewriteSkillWithAI(formData, request),
      onApply: (rewritten) => setFormData((current) => ({
        ...current,
        name: rewritten.name || current.name,
        description: rewritten.description || current.description,
      })),
    });
  }, [formData, onOpenAI]);

  const detailsComplete = Boolean(formData.name.trim() && formData.description.trim());
  const headerActions = useMemo(() => (
    <button className="btn btn-sm btn-outline-primary fixed-menu-ai-action" type="button" onClick={handleAIRewrite} disabled={saving || !onOpenAI}>
      <i className="bi bi-stars" aria-hidden="true"></i>
      <span>Rewrite with AI</span>
    </button>
  ), [handleAIRewrite, onOpenAI, saving]);

  return (
    <>
      <section className="skills-menu-container" aria-labelledby="skills-panel-title">
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mb-4">
          <div>
            <h2 id="skills-panel-title" className="h4 mb-1">Your skills</h2>
            <p className="text-muted mb-0">
              {skills.length} skill{skills.length === 1 ? '' : 's'} ready to use in your portfolio
            </p>
          </div>
          <div className="d-flex flex-column flex-sm-row gap-2">
          <button
            className="btn btn-primary"
            onClick={handleAddSkill}
            disabled={loading || saving}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Add Skill
          </button>
          </div>
        </div>

        <FeedbackNotice message={error || ''} variant="danger" onDismiss={() => setError(null)} />
        <FeedbackNotice message={successMessage || ''} variant="success" onDismiss={() => setSuccessMessage(null)} />

        {/* Loading State */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : skills.length === 0 ? (
          <div className="text-center text-muted py-3">
            <small>Add abilities relevant to your desired jobs so the AI knows what strengths to highlight.</small>
          </div>
        ) : (
          /* Skills List */
          <div className="row g-3">
            {skills.map((skill) => (
              <div key={skill.id} className="col-12 col-md-6 col-lg-4">
                <article className="card h-100 border-secondary-subtle shadow-sm">
                  <div className="card-body d-flex flex-column p-3">
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <IconDisplay icon={skill.icon} userId={userId} label={skill.name} size={44} />
                      <div className="min-w-0 flex-grow-1">
                        <h3 className="h6 card-title mb-1 text-break">{skill.name}</h3>
                        <div className="d-flex gap-1 text-warning small" aria-label={`${skill.rating} out of 5 stars`}>
                          {Array.from({ length: 5 }, (_, index) => (
                            <i key={index} className={`bi ${index < skill.rating ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="card-text text-muted mb-3 flex-grow-1">{skill.description}</p>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEditSkill(skill)}
                        disabled={saving}
                      >
                        <i className="bi bi-pencil me-2" aria-hidden="true"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteSkill(skill.id, skill.name)}
                        title={`Delete ${skill.name}`}
                        aria-label={`Delete ${skill.name}`}
                        disabled={saving}
                      >
                        <i className="bi bi-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        )}
      </section>

      <FixedMenu
        title={editingSkill ? `Edit ${editingSkill.name}` : 'Add Skill'}
        iconClass={editingSkill ? 'bi-pencil-square' : 'bi-plus-lg'}
        headerActions={headerActions}
        expanded={showForm && !assistantOpen}
        setExpanded={(nextExpanded) => {
          if (!nextExpanded) handleCancelForm();
        }}
        animate
      >
        <form className="portfolio-record-form portfolio-tabbed-form" onSubmit={handleSubmit} noValidate>
          <div className="portfolio-form-tabs" role="tablist" aria-label="Skill form sections">
            <button id="skill-details-tab" type="button" role="tab" aria-selected={activeFormTab === 'details'} aria-controls="skill-details-panel" className={activeFormTab === 'details' ? 'active' : ''} onClick={() => setActiveFormTab('details')}>
              Details
              {fieldErrors.name || fieldErrors.description ? <i className="bi bi-exclamation-circle-fill text-danger" aria-label="Needs attention"></i> : detailsComplete ? <i className="bi bi-check-circle-fill text-success" aria-label="Complete"></i> : null}
            </button>
            <button id="skill-appearance-tab" type="button" role="tab" aria-selected={activeFormTab === 'appearance'} aria-controls="skill-appearance-panel" className={activeFormTab === 'appearance' ? 'active' : ''} onClick={() => setActiveFormTab('appearance')}>
              Appearance
              <i className="bi bi-check-circle-fill text-success" aria-label="Complete"></i>
            </button>
          </div>

          {activeFormTab === 'details' ? <div className="portfolio-form-tab-panel" id="skill-details-panel" role="tabpanel" aria-labelledby="skill-details-tab">
            <div className="portfolio-form-section-heading">
              <span className="portfolio-form-section-icon"><i className="bi bi-card-text" aria-hidden="true"></i></span>
              <div><h4>Core details</h4><p>Fields marked required must be completed.</p></div>
            </div>
          <div className="mb-3">
            <label htmlFor="skillName" className="form-label fw-semibold">Skill name <span className="text-danger" aria-hidden="true">*</span></label>
            <input
              type="text"
              className="form-control"
              id="skillName"
              name="name"
              ref={nameInputRef}
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., React"
              maxLength={80}
              aria-invalid={Boolean(fieldErrors.name)}
              aria-describedby={fieldErrors.name ? 'skillNameError' : 'skillNameHelp'}
              disabled={saving}
            />
            {fieldErrors.name ? <div className="invalid-feedback d-block" id="skillNameError">{fieldErrors.name}</div> : <div className="form-text" id="skillNameHelp">Use a name people will quickly recognise.</div>}
          </div>

          <div className="mb-4">
            <label htmlFor="skillDescription" className="form-label fw-semibold">Description <span className="text-danger" aria-hidden="true">*</span></label>
            <textarea
              className="form-control"
              id="skillDescription"
              name="description"
              ref={descriptionInputRef}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="e.g., Building interactive user interfaces"
              rows={5}
              maxLength={500}
              aria-invalid={Boolean(fieldErrors.description)}
              disabled={saving}
            />
            <div className="form-text d-flex justify-content-between gap-2"><span>Focus on what you can do with this skill.</span><span>{formData.description.length}/500</span></div>
          </div>
          </div> : null}

          {activeFormTab === 'appearance' ? <div className="portfolio-form-tab-panel" id="skill-appearance-panel" role="tabpanel" aria-labelledby="skill-appearance-tab">
            <div className="portfolio-form-section-heading">
              <span className="portfolio-form-section-icon"><i className="bi bi-palette" aria-hidden="true"></i></span>
              <div><h4>Presentation</h4><p>Choose an icon and confidence level.</p></div>
            </div>
          <div className="mb-4">
            <IconPicker
              userId={userId}
              value={formData.icon}
              onChange={(icon) => setFormData((current) => ({ ...current, icon }))}
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="skillRating" className="form-label fw-semibold">Confidence level</label>
            <div className="skill-rating-picker" id="skillRating" role="group" aria-label="Confidence level">
              {Array.from({ length: 5 }, (_, index) => {
                const rating = index + 1;
                return (
                  <button key={rating} type="button" className={rating <= formData.rating ? 'is-active' : ''}
                    onClick={() => setFormData((current) => ({ ...current, rating }))}
                    aria-label={`${rating} out of 5`} aria-pressed={rating === formData.rating} disabled={saving}>
                    <i className={`bi ${rating <= formData.rating ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true"></i>
                  </button>
                );
              })}
            </div>
            <div className="form-text text-center">{formData.rating} of 5 — choose the level that honestly reflects your experience.</div>
          </div>
          </div> : null}

          <div className="portfolio-record-form-actions d-flex flex-column flex-sm-row gap-2">
            <button type="submit" className="btn btn-success flex-grow-1" disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : editingSkill ? 'Save changes' : 'Add skill'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleCancelForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </FixedMenu>
    </>
  );
};

export default SkillsMenu;
